import classNames from "classnames"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useLayoutEffect,
  useRef,
} from "react"
import styles from "./SliderValue.module.scss"

import { useSliderValueContext } from "./SliderValueContext"

export interface Props extends ComponentPropsWithoutRef<"div"> {
  unit?: string;
}

export const SliderValueLabelContainer = forwardRef<HTMLDivElement, Props>(
  function SliderValueLabelContainer(props: Props, propRef: Ref<HTMLDivElement>) {
    const {
      unit,
      ...htmlLabelProps
    } = props

    const {
      max,
      placeholder,
      setHasLabel,
      setUnitWidth,
      sizePresetKey,
      value,
    } = useSliderValueContext()
    const unitRef = useRef<HTMLSpanElement>(null)

    // update the stored unit width if the unit or sizePreset change
    useLayoutEffect(
      () => {
        const unitElement = unitRef.current
        if (unitElement) {
          setUnitWidth(unitElement.clientWidth)
        }
      },
      [
        setUnitWidth,
        sizePresetKey,
        unit,
        unitRef,
      ],
    )

    // update hasLabel when the children change
    useLayoutEffect(
      () => {
        setHasLabel(!!htmlLabelProps.children)
      },
      [
        htmlLabelProps.children,
        setHasLabel,
      ],
    )

    const valueRowClassName = classNames(
      styles.valueRow,
      {
        [styles.valueRowNoUnits]: unit === undefined,
      },
    )

    const className = classNames(
      props.className,
      styles.sliderValueLabelContainer,
    )

    const maxString = `${max || "0"}`.replace(
      /./g,
      "0",
    )

    return (
      <div
        className={className}
        ref={propRef}
      >
        <div className={valueRowClassName}>
          <div className={styles.valueRowMax}>
            <span className={styles.valueRowMaxValue}>
              {maxString}
            </span>
            {unit && (
              <span className={styles.valueRowMaxUnit}>
                {unit}
              </span>
            )}
          </div>
          <div className={styles.valueRowCurrent}>
            <span className={styles.valueRowCurrentValue}>
              {value === undefined || value === "" ? placeholder : value}
            </span>
            {unit && (
              <span
                className={styles.valueRowCurrentUnit}
                ref={unitRef}
              >
                {unit}
              </span>
            )}
          </div>
        </div>
        {props.children}
      </div>
    )
  },
)
